import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Alert, CircularProgress, Avatar, Tooltip, IconButton,
  Divider, LinearProgress,
} from '@mui/material';
import {
  AttachMoney, Add, Edit, Delete, TrendingUp, TrendingDown,
  TrendingFlat, ArrowUpward, ArrowDownward,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const CHANGE_TYPES: Record<string, { label: string; color: string; emoji: string }> = {
  hire:           { label: 'New Hire',        color: '#1565c0', emoji: '🚀' },
  promotion:      { label: 'Promotion',       color: '#2e7d32', emoji: '📈' },
  annual_review:  { label: 'Annual Review',   color: '#4527a0', emoji: '📅' },
  market_adjust:  { label: 'Market Adjust',   color: '#00838f', emoji: '📊' },
  performance:    { label: 'Performance',     color: '#f57f17', emoji: '⭐' },
  restructure:    { label: 'Restructure',     color: '#880e4f', emoji: '🏢' },
  correction:     { label: 'Correction',      color: '#78909c', emoji: '✏️' },
  counter_offer:  { label: 'Counter Offer',   color: '#e65100', emoji: '🤝' },
  other:          { label: 'Other',           color: '#546e7a', emoji: '📋' },
};

const CURRENCIES = ['HKD', 'USD', 'SGD', 'EUR', 'GBP'];
const FREQUENCIES = ['monthly', 'annual', 'hourly'];

interface SalaryRecord {
  id: string; employeeId: string; effectiveDate: string;
  amount: number; currency: string; frequency: string;
  previousAmount?: number; previousCurrency?: string;
  changeType: string; reason?: string; notes?: string;
  isActive: boolean; createdAt: string;
  recordedBy?: { firstName: string; lastName: string };
}

interface OrgRow {
  employeeId: string; name: string; department: string; position: string;
  salary: number; currency: string; frequency: string;
  previousAmount?: number; changeType: string; lastChangeDate: string;
  changePercent?: number;
}

interface Stats {
  employeesWithSalary: string; totalRecords: string;
  changesLast30d: string; promotions: string; avgSalaryHKD: string;
  byType: { changeType: string; count: string }[];
}

const emptyForm = {
  employeeId: '', effectiveDate: '', amount: '',
  currency: 'HKD', frequency: 'monthly',
  changeType: 'other', reason: '', notes: '',
};

function fmt(amount: number, currency: string) {
  return `${currency} ${Math.round(amount).toLocaleString()}`;
}

function ChangeIcon({ pct }: { pct?: number }) {
  if (!pct) return <TrendingFlat sx={{ color: '#78909c', fontSize: '1rem' }} />;
  if (pct > 0) return <TrendingUp sx={{ color: '#2e7d32', fontSize: '1rem' }} />;
  return <TrendingDown sx={{ color: '#c62828', fontSize: '1rem' }} />;
}

export default function SalaryHistory() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes((user as any)?.role || '');
  const isFinance = (user as any)?.role === 'finance';
  const canViewAll = isHR || isFinance;

  const [tab, setTab] = useState(0);
  const [overview, setOverview] = useState<OrgRow[]>([]);
  const [myHistory, setMyHistory] = useState<SalaryRecord[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [deptFilter, setDeptFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Selected employee for drill-down
  const [selectedEmp, setSelectedEmp] = useState<OrgRow | null>(null);
  const [empHistory, setEmpHistory] = useState<SalaryRecord[]>([]);

  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [editId, setEditId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<SalaryRecord | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const calls: Promise<any>[] = [apiFetch('/api/salary-history/me/current')];
      if (canViewAll) {
        const params = deptFilter ? `?departmentId=${deptFilter}` : '';
        calls.push(apiFetch(`/api/salary-history/overview${params}`));
        calls.push(apiFetch('/api/salary-history/stats'));
        calls.push(apiFetch('/api/employees?limit=300'));
        calls.push(apiFetch('/api/departments'));
      }
      const results = await Promise.allSettled(calls);
      // my current — we use it below via myHistory call
      if (canViewAll) {
        if (results[1].status === 'fulfilled') setOverview(results[1].value ?? []);
        if (results[2].status === 'fulfilled') setStats(results[2].value);
        if (results[3].status === 'fulfilled') {
          const d = results[3].value; setEmployees(Array.isArray(d) ? d : (d.data ?? []));
        }
        if (results[4].status === 'fulfilled') setDepartments(results[4].value ?? []);
      }
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [canViewAll, deptFilter]);

  // My salary history
  useEffect(() => {
    const empId = (user as any)?.employee?.id;
    if (empId) apiFetch(`/api/salary-history/employee/${empId}`).then(d => setMyHistory(d)).catch(() => {});
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const loadEmpHistory = async (empId: string) => {
    const h = await apiFetch(`/api/salary-history/employee/${empId}`);
    setEmpHistory(h);
  };

  const openCreate = (preEmpId?: string) => {
    setEditId(null);
    setFormData({ ...emptyForm, employeeId: preEmpId || '' });
    setFormOpen(true);
  };

  const openEdit = (r: SalaryRecord) => {
    setEditId(r.id);
    setFormData({
      employeeId: r.employeeId, effectiveDate: r.effectiveDate,
      amount: r.amount.toString(), currency: r.currency, frequency: r.frequency,
      changeType: r.changeType, reason: r.reason || '', notes: r.notes || '',
    });
    setFormOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...formData, amount: Number(formData.amount) };
      if (editId) {
        await apiFetch(`/api/salary-history/${editId}`, { method: 'PATCH', body: JSON.stringify(payload) });
      } else {
        await apiFetch('/api/salary-history', { method: 'POST', body: JSON.stringify(payload) });
      }
      setFormOpen(false);
      load();
      if (selectedEmp) loadEmpHistory(selectedEmp.employeeId);
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    await apiFetch(`/api/salary-history/${deleteConfirm.id}`, { method: 'DELETE' });
    setDeleteConfirm(null);
    load();
    if (selectedEmp) loadEmpHistory(selectedEmp.employeeId);
  };

  const filtered = overview.filter(r => !deptFilter || r.department === departments.find((d: any) => d.id === deptFilter)?.name);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#1b5e20', width: 48, height: 48 }}><AttachMoney /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Salary History</Typography>
            <Typography variant="body2" color="text.secondary">
              Track compensation changes over time
            </Typography>
          </Box>
        </Box>
        {isHR && (
          <Button variant="contained" startIcon={<Add />} onClick={() => openCreate()}
            sx={{ bgcolor: '#1b5e20', '&:hover': { bgcolor: '#1b5e20cc' } }}>
            Add Record
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats (HR/Finance) */}
      {canViewAll && stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Employees on File', value: stats.employeesWithSalary, color: '#1b5e20' },
            { label: 'Changes (30d)', value: stats.changesLast30d, color: '#f57f17' },
            { label: 'Promotions Total', value: stats.promotions, color: '#2e7d32' },
            { label: 'Avg Salary (HKD)', value: stats.avgSalaryHKD ? `$${parseInt(stats.avgSalaryHKD).toLocaleString()}` : '—', color: '#1565c0' },
          ].map(s => (
            <Grid size={{ xs: 6, sm: 3 }} key={s.label}>
              <Paper sx={{ p: 2, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
                <Typography variant="h5" fontWeight={700} color={s.color}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="My Salary" />
        {canViewAll && <Tab label="Org Overview" />}
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* My Salary History */}
          {tab === 0 && (
            <Paper>
              <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                <Typography variant="subtitle1" fontWeight={700}>My Compensation History</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Effective Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Change</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {myHistory.length === 0 && (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No salary records on file
                      </TableCell></TableRow>
                    )}
                    {myHistory.map(r => {
                      const pct = r.previousAmount ? ((r.amount - r.previousAmount) / r.previousAmount) * 100 : undefined;
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell>{r.effectiveDate}</TableCell>
                          <TableCell>
                            <Typography fontWeight={700}>{fmt(r.amount, r.currency)}</Typography>
                            <Typography variant="caption" color="text.secondary">/ {r.frequency}</Typography>
                          </TableCell>
                          <TableCell>
                            {r.previousAmount ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ChangeIcon pct={pct} />
                                <Typography variant="body2" color={pct! > 0 ? 'success.main' : pct! < 0 ? 'error.main' : 'text.secondary'}>
                                  {pct! > 0 ? '+' : ''}{pct?.toFixed(1)}%
                                </Typography>
                              </Box>
                            ) : '—'}
                          </TableCell>
                          <TableCell>
                            <Chip size="small"
                              label={`${CHANGE_TYPES[r.changeType]?.emoji} ${CHANGE_TYPES[r.changeType]?.label ?? r.changeType}`}
                              sx={{ bgcolor: (CHANGE_TYPES[r.changeType]?.color ?? '#666') + '15',
                                color: CHANGE_TYPES[r.changeType]?.color ?? '#666', fontSize: '0.7rem' }} />
                          </TableCell>
                          <TableCell><Typography variant="caption">{r.reason || '—'}</Typography></TableCell>
                          <TableCell>
                            {r.isActive ? <Chip size="small" label="Current" color="success" /> :
                              <Chip size="small" label="Historical" variant="outlined" sx={{ color: '#78909c' }} />}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Org Overview */}
          {canViewAll && tab === 1 && (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField select size="small" label="Filter by Department" value={deptFilter}
                  onChange={e => setDeptFilter(e.target.value)} sx={{ minWidth: 200 }}>
                  <MenuItem value="">All Departments</MenuItem>
                  {departments.map((d: any) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                </TextField>
              </Box>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Position</TableCell>
                      <TableCell align="right">Current Salary</TableCell>
                      <TableCell align="center">Change</TableCell>
                      <TableCell>Last Change</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.length === 0 && (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No salary records found
                      </TableCell></TableRow>
                    )}
                    {filtered.map(row => {
                      const pct = row.changePercent;
                      return (
                        <TableRow key={row.employeeId} hover sx={{ cursor: 'pointer' }}
                          onClick={() => { setSelectedEmp(row); loadEmpHistory(row.employeeId); }}>
                          <TableCell><Typography variant="body2" fontWeight={600}>{row.name}</Typography></TableCell>
                          <TableCell><Typography variant="caption">{row.department}</Typography></TableCell>
                          <TableCell><Typography variant="caption">{row.position}</Typography></TableCell>
                          <TableCell align="right">
                            {row.salary ? (
                              <Typography fontWeight={700}>{fmt(row.salary, row.currency)}</Typography>
                            ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                          </TableCell>
                          <TableCell align="center">
                            {pct != null ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                {pct > 0 ? <ArrowUpward sx={{ color: '#2e7d32', fontSize: '0.9rem' }} /> :
                                 pct < 0 ? <ArrowDownward sx={{ color: '#c62828', fontSize: '0.9rem' }} /> :
                                 <TrendingFlat sx={{ color: '#78909c', fontSize: '0.9rem' }} />}
                                <Typography variant="body2"
                                  color={pct > 0 ? 'success.main' : pct < 0 ? 'error.main' : 'text.secondary'}>
                                  {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                                </Typography>
                              </Box>
                            ) : '—'}
                          </TableCell>
                          <TableCell><Typography variant="caption">{row.lastChangeDate || '—'}</Typography></TableCell>
                          <TableCell>
                            {row.changeType && (
                              <Chip size="small"
                                label={`${CHANGE_TYPES[row.changeType]?.emoji ?? ''} ${CHANGE_TYPES[row.changeType]?.label ?? row.changeType}`}
                                sx={{ bgcolor: (CHANGE_TYPES[row.changeType]?.color ?? '#666') + '15',
                                  color: CHANGE_TYPES[row.changeType]?.color ?? '#666', fontSize: '0.65rem' }} />
                            )}
                          </TableCell>
                          <TableCell align="center" onClick={e => e.stopPropagation()}>
                            <Tooltip title="Add salary record">
                              <IconButton size="small" onClick={() => openCreate(row.employeeId)}>
                                <Add fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}

      {/* Employee Salary History Drill-down Dialog */}
      <Dialog open={!!selectedEmp} onClose={() => setSelectedEmp(null)} maxWidth="md" fullWidth>
        {selectedEmp && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{selectedEmp.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedEmp.department} · {selectedEmp.position}</Typography>
                </Box>
                {isHR && (
                  <Button size="small" startIcon={<Add />} variant="outlined"
                    onClick={() => openCreate(selectedEmp.employeeId)}>
                    Add Record
                  </Button>
                )}
              </Box>
            </DialogTitle>
            <DialogContent>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Effective Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="center">Change</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Status</TableCell>
                      {isHR && <TableCell />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {empHistory.map(r => {
                      const pct = r.previousAmount ? ((r.amount - r.previousAmount) / r.previousAmount) * 100 : undefined;
                      return (
                        <TableRow key={r.id} hover sx={{ bgcolor: r.isActive ? '#e8f5e920' : undefined }}>
                          <TableCell>{r.effectiveDate}</TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={r.isActive ? 700 : 400}>{fmt(r.amount, r.currency)}</Typography>
                            <Typography variant="caption" color="text.secondary">/{r.frequency}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            {pct != null ? (
                              <Typography variant="body2" color={pct > 0 ? 'success.main' : pct < 0 ? 'error.main' : 'text.secondary'}>
                                {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                              </Typography>
                            ) : '—'}
                          </TableCell>
                          <TableCell>
                            <Chip size="small"
                              label={`${CHANGE_TYPES[r.changeType]?.emoji} ${CHANGE_TYPES[r.changeType]?.label ?? r.changeType}`}
                              sx={{ bgcolor: (CHANGE_TYPES[r.changeType]?.color ?? '#666') + '15',
                                color: CHANGE_TYPES[r.changeType]?.color ?? '#666', fontSize: '0.65rem' }} />
                          </TableCell>
                          <TableCell><Typography variant="caption">{r.reason || '—'}</Typography></TableCell>
                          <TableCell>
                            {r.isActive
                              ? <Chip size="small" label="Current" color="success" />
                              : <Chip size="small" label="Past" variant="outlined" sx={{ color: '#999' }} />}
                          </TableCell>
                          {isHR && (
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton>
                                <IconButton size="small" color="error" onClick={() => setDeleteConfirm(r)}><Delete fontSize="small" /></IconButton>
                              </Box>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedEmp(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Add / Edit Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edit Salary Record' : 'Add Salary Record'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {!editId && (
            <TextField select fullWidth label="Employee" value={formData.employeeId}
              onChange={e => setFormData(p => ({ ...p, employeeId: e.target.value }))} required>
              {employees.map(e => <MenuItem key={e.id} value={e.id}>{e.firstName} {e.lastName}</MenuItem>)}
            </TextField>
          )}
          <TextField fullWidth label="Effective Date" type="date" InputLabelProps={{ shrink: true }}
            value={formData.effectiveDate} onChange={e => setFormData(p => ({ ...p, effectiveDate: e.target.value }))} required />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Amount" type="number" value={formData.amount}
              onChange={e => setFormData(p => ({ ...p, amount: e.target.value }))} required />
            <TextField select label="Currency" value={formData.currency}
              onChange={e => setFormData(p => ({ ...p, currency: e.target.value }))} sx={{ minWidth: 90 }}>
              {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField select label="Frequency" value={formData.frequency}
              onChange={e => setFormData(p => ({ ...p, frequency: e.target.value }))} sx={{ minWidth: 110 }}>
              {FREQUENCIES.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
            </TextField>
          </Box>
          <TextField select fullWidth label="Change Type" value={formData.changeType}
            onChange={e => setFormData(p => ({ ...p, changeType: e.target.value }))}>
            {Object.entries(CHANGE_TYPES).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>
            ))}
          </TextField>
          <TextField fullWidth label="Reason" value={formData.reason}
            onChange={e => setFormData(p => ({ ...p, reason: e.target.value }))} />
          <TextField fullWidth label="Internal Notes" multiline rows={2} value={formData.notes}
            onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}
            disabled={saving || !formData.amount || !formData.effectiveDate}
            sx={{ bgcolor: '#1b5e20' }}>
            {saving ? 'Saving…' : editId ? 'Update' : 'Add Record'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Record?</DialogTitle>
        <DialogContent>
          <Typography>Delete salary record of <strong>{deleteConfirm && fmt(deleteConfirm.amount, deleteConfirm.currency)}</strong> effective {deleteConfirm?.effectiveDate}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
